import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OrganisationService } from "../../services/OrganisationService";

export const useOrganisations = () => {
  return useQuery({
    queryKey: ["organisations"],
    queryFn: OrganisationService.fetchOrganisations,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useOrganisation = (orgId) => {
  return useQuery({
    queryKey: ["organisation", orgId],
    queryFn: () => OrganisationService.fetchOrganisation(orgId),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrganisationRoles = (orgId) => {
  return useQuery({
    queryKey: ["organisationRoles", orgId],
    queryFn: () => OrganisationService.fetchOrganisationRoles(orgId),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrganisationUsers = (orgId) => {
  return useQuery({
    queryKey: ["organisationUsers", orgId],
    queryFn: () => OrganisationService.fetchOrganisationUsers(orgId),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useCreateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: OrganisationService.createOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
    },
  });
};

export const useUpdateOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }) =>
      OrganisationService.updateOrganisation(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      queryClient.invalidateQueries({ queryKey: ["organisation", orgId] });
    },
  });
};

export const useDeleteOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: OrganisationService.deleteOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
    },
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, userData }) =>
      OrganisationService.inviteUser(orgId, userData),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ["organisationUsers", orgId] });
    },
  });
};

export const useRemoveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, userId }) =>
      OrganisationService.removeUser(orgId, userId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ["organisationUsers", orgId] });
    },
  });
};

export const useUpdateUserRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roles, orgId }) =>
      OrganisationService.setOrganisationMemberRoles(roles, orgId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ["organisationUsers", orgId] });
      queryClient.invalidateQueries({ queryKey: ["organisationRoles", orgId] });
    },
  });
};
